import { useCallback, useEffect, useRef, useState } from "react"
import { importLibrary } from "@googlemaps/js-api-loader"
import Animate from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import InputField from "../InputField/InputField"
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"

interface MapPickerProps {
  onSelect: (data: {
    latitude: number
    longitude: number
    formattedAddress?: string
    addressComponents?: google.maps.GeocoderAddressComponent[]
  }) => void

  initialLatitude?: number
  initialLongitude?: number
}

const FALLBACK_CENTER = {
  lat: -23.55052,
  lng: -46.633308,
}

type PlacesLibrary = google.maps.PlacesLibrary & {
  AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion
}

const mapAddressComponents = (
  addressComponents: google.maps.places.AddressComponent[] | undefined,
): google.maps.GeocoderAddressComponent[] | undefined => {
  if (!addressComponents) {
    return undefined
  }

  return addressComponents.map((component) => ({
    long_name: component.longText ?? "",
    short_name: component.shortText ?? component.longText ?? "",
    types: component.types,
  }))
}

export const DeliveryMapPicker: React.FC<MapPickerProps> = ({ onSelect, initialLatitude, initialLongitude }) => {
  const { t } = useTranslation()

  const onSelectRef = useRef(onSelect)

  const mapRef = useRef<HTMLDivElement | null>(null)

  const mapInstanceRef = useRef<google.maps.Map | null>(null)

  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)

  const geocoderRef = useRef<google.maps.Geocoder | null>(null)

  const autocompleteSuggestionRef = useRef<typeof google.maps.places.AutocompleteSuggestion | null>(null)

  const autocompleteSessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  const autocompleteRequestIdRef = useRef(0)

  const selectedSearchTextRef = useRef("")

  const handleLocationChangeRef = useRef<((latLng: google.maps.LatLng) => void) | null>(null)

  const [isMapReady, setIsMapReady] = useState(false)

  const [isRefreshingLocation, setIsRefreshingLocation] = useState(false)

  const [searchText, setSearchText] = useState("")

  const [addressPredictions, setAddressPredictions] = useState<google.maps.places.PlacePrediction[]>([])

  const [isSearchingAddress, setIsSearchingAddress] = useState(false)

  useEffect(() => {
    onSelectRef.current = onSelect
  }, [onSelect])

  const getUserLocation = useCallback((): Promise<{ lat: number; lng: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("Geolocation not supported")

        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        },
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
        },
      )
    })
  }, [])

  const applySelectedPosition = useCallback(
    (
      latLng: google.maps.LatLng,
      formattedAddress: string | undefined,
      addressComponents: google.maps.GeocoderAddressComponent[] | undefined,
    ) => {
      if (!mapInstanceRef.current || !markerRef.current) {
        return
      }

      const position = {
        lat: latLng.lat(),
        lng: latLng.lng(),
      }

      mapInstanceRef.current.panTo(position)
      mapInstanceRef.current.setZoom(16)
      markerRef.current.position = position

      onSelectRef.current({
        latitude: position.lat,
        longitude: position.lng,
        formattedAddress,
        addressComponents,
      })
    },
    [],
  )

  const ensureAutocompleteSessionToken = useCallback(() => {
    if (!autocompleteSessionTokenRef.current) {
      autocompleteSessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
    }

    return autocompleteSessionTokenRef.current
  }, [])

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) {
      return
    }

    const initMap = async () => {
      const { Map } = (await importLibrary("maps")) as google.maps.MapsLibrary

      const { AdvancedMarkerElement } = (await importLibrary("marker")) as google.maps.MarkerLibrary

      const { AutocompleteSessionToken, AutocompleteSuggestion } = (await importLibrary("places")) as PlacesLibrary

      let center = FALLBACK_CENTER

      if (typeof initialLatitude === "number" && typeof initialLongitude === "number") {
        center = {
          lat: initialLatitude,
          lng: initialLongitude,
        }
      } else {
        try {
          center = await getUserLocation()
        } catch {
          center = FALLBACK_CENTER
        }
      }

      const map = new Map(mapRef.current!, {
        center,
        zoom: 16,
        mapId: import.meta.env.VITE_GOOGLE_MAP_ID,
      })

      const marker = new AdvancedMarkerElement({
        map,
        position: center,
        gmpDraggable: true,
      })

      mapInstanceRef.current = map

      markerRef.current = marker

      autocompleteSuggestionRef.current = AutocompleteSuggestion

      autocompleteSessionTokenRef.current = new AutocompleteSessionToken()

      const geocoder = new google.maps.Geocoder()

      geocoderRef.current = geocoder

      const handleLocationChange = (latLng: google.maps.LatLng) => {
        const latitude = latLng.lat()
        const longitude = latLng.lng()

        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === "OK" && results?.[0]) {
            onSelectRef.current({
              latitude,
              longitude,
              formattedAddress: results[0].formatted_address,
              addressComponents: results[0].address_components,
            })
          } else {
            onSelectRef.current({ latitude, longitude })
          }
        })
      }

      handleLocationChangeRef.current = handleLocationChange

      map.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return
        marker.position = e.latLng
        handleLocationChange(e.latLng)
      })

      marker.addListener("dragend", (e: { latLng?: google.maps.LatLng }) => {
        if (!e.latLng) return
        handleLocationChange(e.latLng)
      })

      handleLocationChange(new google.maps.LatLng(center.lat, center.lng))
      setIsMapReady(true)
    }

    initMap()
  }, [getUserLocation, initialLatitude, initialLongitude])

  useEffect(() => {
    const normalizedSearchText = searchText.trim()

    if (normalizedSearchText === selectedSearchTextRef.current) {
      setAddressPredictions([])
      setIsSearchingAddress(false)
      return
    }

    if (normalizedSearchText.length < 3 || !autocompleteSuggestionRef.current || !isMapReady) {
      setAddressPredictions([])
      setIsSearchingAddress(false)
      return
    }

    const requestId = autocompleteRequestIdRef.current + 1
    autocompleteRequestIdRef.current = requestId
    setIsSearchingAddress(true)

    const timeout = setTimeout(() => {
      autocompleteSuggestionRef.current
        ?.fetchAutocompleteSuggestions({
          input: normalizedSearchText,
          includedRegionCodes: ["br"],
          language: "pt-BR",
          region: "br",
          sessionToken: ensureAutocompleteSessionToken(),
        })
        .then(({ suggestions }) => {
          if (requestId !== autocompleteRequestIdRef.current) {
            return
          }

          setIsSearchingAddress(false)

          setAddressPredictions(
            suggestions
              .map((suggestion) => suggestion.placePrediction)
              .filter((prediction): prediction is google.maps.places.PlacePrediction => prediction !== null)
              .slice(0, 5),
          )
        })
        .catch(() => {
          if (requestId !== autocompleteRequestIdRef.current) {
            return
          }

          setIsSearchingAddress(false)
          setAddressPredictions([])
        })
    }, 350)

    return () => clearTimeout(timeout)
  }, [searchText, isMapReady, ensureAutocompleteSessionToken])

  const applyInitialPosition = useCallback(() => {
    if (!mapInstanceRef.current) {
      return
    }

    if (!markerRef.current) {
      return
    }

    if (typeof initialLatitude !== "number") {
      return
    }

    if (typeof initialLongitude !== "number") {
      return
    }

    const newCenter = {
      lat: initialLatitude,
      lng: initialLongitude,
    }

    mapInstanceRef.current.setCenter(newCenter)
    markerRef.current.position = newCenter
  }, [initialLatitude, initialLongitude])

  useEffect(() => {
    const timeout = setTimeout(() => {
      applyInitialPosition()
    }, 100)

    return () => clearTimeout(timeout)
  }, [applyInitialPosition])

  const handleRefreshGeolocation = async () => {
    if (!mapInstanceRef.current || !markerRef.current || !geocoderRef.current || !handleLocationChangeRef.current) {
      return
    }

    setIsRefreshingLocation(true)

    try {
      const center = await getUserLocation()

      mapInstanceRef.current.setCenter(center)

      markerRef.current.position = center

      handleLocationChangeRef.current(new google.maps.LatLng(center.lat, center.lng))
    } finally {
      setIsRefreshingLocation(false)
    }
  }

  const handleSelectAddressPrediction = async (prediction: google.maps.places.PlacePrediction) => {
    const selectedText = prediction.text.text

    selectedSearchTextRef.current = selectedText
    autocompleteRequestIdRef.current += 1
    setSearchText(selectedText)
    setAddressPredictions([])
    setIsSearchingAddress(true)

    try {
      const place = prediction.toPlace()
      const details = await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "location"],
      })

      if (!details.place.location) {
        return
      }

      applySelectedPosition(
        details.place.location,
        details.place.formattedAddress ?? prediction.text.text,
        mapAddressComponents(details.place.addressComponents),
      )
    } finally {
      setIsSearchingAddress(false)
      autocompleteSessionTokenRef.current = null
    }
  }

  return (
    <Animate duration="2s">
      <div className="w-full">
        <div className="relative mb-3">
          <InputField
            type="text"
            value={searchText}
            icon={faMagnifyingGlass}
            placeholder={t("address:label_search_address_placeholder")}
            onInvalidMessage={t("address:label_search_address_invalid")}
            onChange={(event) => {
              selectedSearchTextRef.current = ""
              setSearchText(event.target.value)
            }}
            maxLength={180}
            disable={!isMapReady}
            descriptionKey="interactionDescriptions:components.deliveryMapPicker.deliveryMapPicker.input.searchAddressPlaceholder"
          />

          {(isSearchingAddress || addressPredictions.length > 0) && (
            <div className="absolute left-0 right-0 z-20 mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
              {isSearchingAddress && (
                <div className="px-4 py-3 text-sm font-semibold text-gray-500">
                  {t("address:label_searching_address")}
                </div>
              )}

              {!isSearchingAddress &&
                addressPredictions.map((prediction) => (
                  <button
                    key={prediction.placeId}
                    type="button"
                    onClick={() => handleSelectAddressPrediction(prediction)}
                    className="block w-full border-b border-gray-100 px-4 py-3 text-left text-sm font-semibold text-gray-700 transition last:border-b-0 hover:bg-orange-50 hover:text-orange-700"
                    data-interaction-description-key="interactionDescriptions:components.deliveryMapPicker.deliveryMapPicker.button.handleSelectAddressPredictionPrediction"
                  >
                    {prediction.text.text}
                  </button>
                ))}
            </div>
          )}
        </div>

        <div ref={mapRef} className="w-full h-[400px] rounded-lg shadow-md" />

        {isMapReady && (
          <div className="flex justify-center mt-3 px-2">
            <button
              type="button"
              onClick={handleRefreshGeolocation}
              disabled={isRefreshingLocation}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              data-interaction-description-key="interactionDescriptions:components.deliveryMapPicker.deliveryMapPicker.button.handleRefreshGeolocation"
            >
              {isRefreshingLocation ? t("address:label_refreshing_geolocation") : t("address:label_retry_geolocation")}
            </button>
          </div>
        )}

        <p className="text-xs text-center mt-2 font-bold text-gray-600 p-2">{t("address:label_select_map")}</p>
      </div>
    </Animate>
  )
}
